import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_OPTIONS = ["available", "coming_soon", "sold_out"];

const emptyProduct = () => ({
    id: "",
    name: "",
    description: "",
    price: 0,
    image: "",
    images: [],
    category: "",
    status: "available",
    sizes: ["S", "M", "L", "XL"],
    colors: [],
});

function resolveImg(src) {
    if (!src) return "";
    if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
    return src; // /products/foo.webp etc. served by frontend
}

function StatusBadge({ status }) {
    const map = {
        available: { color: "#22c55e", label: "Live" },
        coming_soon: { color: "#3b82f6", label: "Coming Soon" },
        sold_out: { color: "#71717a", label: "Sold Out" },
    };
    const v = map[status] || { color: "#71717a", label: status };
    return (
        <span
            className="inline-block font-display text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 border"
            style={{ color: v.color, borderColor: `${v.color}80` }}
        >
            ● {v.label}
        </span>
    );
}

function ProductCard({ p, onEdit, onReset, onDelete }) {
    return (
        <div
            data-testid={`product-card-${p.id}`}
            className="bg-fgm-surface border border-white/10 hover:border-white/30 transition-colors flex flex-col"
        >
            <div className="aspect-square bg-black overflow-hidden">
                {p.image && (
                    <img
                        src={resolveImg(p.image)}
                        alt={p.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                    />
                )}
            </div>
            <div className="p-4 flex flex-col gap-2 flex-1">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-sm tracking-wider text-white leading-snug">
                        {p.name}
                    </h3>
                    <StatusBadge status={p.status} />
                </div>
                <div className="flex items-center gap-2 text-xs">
                    <span className="text-zinc-500 font-mono">${Number(p.price).toFixed(2)}</span>
                    <span className="text-zinc-700">·</span>
                    <span className="text-zinc-500 font-display tracking-[0.2em] uppercase text-[10px]">
                        {p.category}
                    </span>
                </div>
                <div className="text-[10px] font-display tracking-[0.2em] uppercase text-zinc-600">
                    {p.is_custom ? "Custom" : "Seed"}
                    {p.has_overlay && " · Edited"}
                </div>
                <div className="mt-auto pt-3 flex items-center gap-2">
                    <button
                        data-testid={`product-edit-${p.id}`}
                        onClick={() => onEdit(p)}
                        className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white transition-colors flex-1"
                    >
                        Edit
                    </button>
                    {!p.is_custom && p.has_overlay && (
                        <button
                            data-testid={`product-reset-${p.id}`}
                            onClick={() => onReset(p)}
                            className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-white/20 text-zinc-400 hover:text-white"
                            title="Discard edits, restore original"
                        >
                            ↺
                        </button>
                    )}
                    {p.is_custom && (
                        <button
                            data-testid={`product-delete-${p.id}`}
                            onClick={() => onDelete(p)}
                            className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-white/20 text-zinc-500 hover:text-[#E63946] hover:border-[#E63946]"
                            title="Delete custom product"
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ImageField({ label, value, onChange, password, testid }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const handleFile = async (file) => {
        if (!file) return;
        if (file.size > 6 * 1024 * 1024) {
            toast.error("Image must be ≤ 6 MB");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const res = await axios.post(`${API}/admin/products/upload-image`, fd, {
                headers: { "X-Admin-Password": password },
            });
            onChange(res.data.url);
            toast.success(`Uploaded ${file.name}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };
    return (
        <div className="space-y-2">
            <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                {label}
            </label>
            <div className="flex items-stretch gap-2">
                {value && (
                    <div className="w-16 h-16 border border-white/10 bg-black overflow-hidden flex-shrink-0">
                        <img src={resolveImg(value)} alt="" className="w-full h-full object-cover" />
                    </div>
                )}
                <input
                    data-testid={`${testid}-input`}
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="/products/your-image.webp"
                    className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm text-white font-mono"
                />
                <button
                    type="button"
                    data-testid={`${testid}-upload`}
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-2 border border-white/20 text-white hover:bg-white hover:text-black disabled:opacity-50"
                >
                    {uploading ? "…" : "↑ Upload"}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/webp,image/jpeg,image/png"
                    className="hidden"
                    onChange={(e) => {
                        handleFile(e.target.files?.[0]);
                        e.target.value = "";
                    }}
                />
            </div>
        </div>
    );
}

function GalleryEditor({ images, onChange, password }) {
    const setAt = (idx, val) => {
        const next = [...images];
        next[idx] = val;
        onChange(next);
    };
    const remove = (idx) => {
        const next = images.filter((_, i) => i !== idx);
        onChange(next);
    };
    const add = () => onChange([...(images || []), ""]);
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                    Gallery (additional images)
                </label>
                <button
                    type="button"
                    data-testid="gallery-add"
                    onClick={add}
                    className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-400 hover:text-white border border-white/10 px-2 py-1"
                >
                    + Add Slot
                </button>
            </div>
            {(images || []).map((src, idx) => (
                <div key={idx} className="flex items-stretch gap-2">
                    <ImageField
                        label=""
                        value={src}
                        onChange={(v) => setAt(idx, v)}
                        password={password}
                        testid={`gallery-${idx}`}
                    />
                    <button
                        type="button"
                        onClick={() => remove(idx)}
                        className="self-end text-zinc-500 hover:text-[#E63946] px-3 py-2 border border-white/10"
                        aria-label="Remove image"
                    >
                        ×
                    </button>
                </div>
            ))}
            {(!images || images.length === 0) && (
                <p className="text-xs text-zinc-600">No gallery images. Main image will be used alone.</p>
            )}
        </div>
    );
}

function ProductModal({ open, onClose, product, isNew, password, onSaved }) {
    const [form, setForm] = useState(emptyProduct());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (product) {
            setForm({
                id: product.id || "",
                name: product.name || "",
                description: product.description || "",
                price: product.price ?? 0,
                image: product.image || "",
                images: product.images || [],
                category: product.category || "",
                status: product.status || "available",
                sizes: product.sizes || ["S", "M", "L", "XL"],
                colors: product.colors || [],
            });
        } else {
            setForm(emptyProduct());
        }
    }, [open, product]);

    if (!open) return null;

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
    const csv = (arr) => (arr || []).join(", ");
    const fromCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

    const handleSave = async () => {
        // Validate
        if (!form.name.trim()) return toast.error("Name is required");
        if (!form.image.trim()) return toast.error("Main image is required");
        if (!form.category.trim()) return toast.error("Category is required");
        if (!form.description.trim()) return toast.error("Description is required");
        if (!form.price || form.price <= 0) return toast.error("Price must be > 0");

        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                price: Number(form.price),
                image: form.image.trim(),
                images: (form.images || []).map((s) => s.trim()).filter(Boolean),
                category: form.category.trim().toUpperCase(),
                status: form.status,
                sizes: form.sizes,
                colors: form.colors,
            };
            const headers = { "X-Admin-Password": password };
            if (isNew) {
                await axios.post(`${API}/admin/products`, payload, { headers });
                toast.success("Product created");
            } else {
                await axios.put(`${API}/admin/products/${product.id}`, payload, { headers });
                toast.success("Product updated");
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="product-modal"
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="bg-fgm-surface border border-white/10 w-full max-w-3xl my-8">
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <h2 className="font-display text-lg tracking-[0.2em] uppercase text-white">
                        {isNew ? "Create Product" : `Edit · ${product?.name}`}
                    </h2>
                    <button
                        data-testid="product-modal-close"
                        onClick={onClose}
                        className="text-zinc-500 hover:text-white text-2xl leading-none"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 block mb-1">
                                Name
                            </label>
                            <input
                                data-testid="product-form-name"
                                value={form.name}
                                onChange={(e) => set("name", e.target.value)}
                                className="w-full bg-black border border-white/10 px-3 py-2 text-sm text-white"
                            />
                        </div>
                        <div>
                            <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 block mb-1">
                                Price (USD)
                            </label>
                            <input
                                data-testid="product-form-price"
                                type="number"
                                step="0.01"
                                value={form.price}
                                onChange={(e) => set("price", parseFloat(e.target.value) || 0)}
                                className="w-full bg-black border border-white/10 px-3 py-2 text-sm text-white font-mono"
                            />
                        </div>
                        <div>
                            <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 block mb-1">
                                Category
                            </label>
                            <input
                                data-testid="product-form-category"
                                value={form.category}
                                onChange={(e) => set("category", e.target.value)}
                                placeholder="HOODIES · T-SHIRTS · TRAINING · HEADWEAR"
                                className="w-full bg-black border border-white/10 px-3 py-2 text-sm text-white"
                            />
                        </div>
                        <div>
                            <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 block mb-1">
                                Status
                            </label>
                            <select
                                data-testid="product-form-status"
                                value={form.status}
                                onChange={(e) => set("status", e.target.value)}
                                className="w-full bg-black border border-white/10 px-3 py-2 text-sm text-white"
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 block mb-1">
                            Description
                        </label>
                        <textarea
                            data-testid="product-form-description"
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            rows={4}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm text-white"
                        />
                    </div>

                    <ImageField
                        label="Main Image"
                        value={form.image}
                        onChange={(v) => set("image", v)}
                        password={password}
                        testid="product-form-main-image"
                    />

                    <GalleryEditor
                        images={form.images}
                        onChange={(v) => set("images", v)}
                        password={password}
                    />

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 block mb-1">
                                Sizes (comma-separated)
                            </label>
                            <input
                                data-testid="product-form-sizes"
                                value={csv(form.sizes)}
                                onChange={(e) => set("sizes", fromCsv(e.target.value))}
                                placeholder="XS, S, M, L, XL"
                                className="w-full bg-black border border-white/10 px-3 py-2 text-sm text-white"
                            />
                        </div>
                        <div>
                            <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 block mb-1">
                                Colors (comma-separated)
                            </label>
                            <input
                                data-testid="product-form-colors"
                                value={csv(form.colors)}
                                onChange={(e) => set("colors", fromCsv(e.target.value))}
                                placeholder="Black, White, Red"
                                className="w-full bg-black border border-white/10 px-3 py-2 text-sm text-white"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-white/10 px-6 py-4">
                    <button
                        onClick={onClose}
                        className="text-[10px] font-display tracking-[0.25em] uppercase px-4 py-2 border border-white/15 text-zinc-300 hover:text-white"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="product-form-save"
                        onClick={handleSave}
                        disabled={saving}
                        className="text-[10px] font-display tracking-[0.25em] uppercase px-5 py-2 bg-[#E63946] text-white hover:bg-white hover:text-[#E63946] disabled:opacity-50"
                    >
                        {saving ? "Saving…" : isNew ? "Create Product" : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ProductsTab({ password }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isNew, setIsNew] = useState(false);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/products`, {
                headers: { "X-Admin-Password": password },
            });
            setProducts(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load products");
        } finally {
            setLoading(false);
        }
    }, [password]);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(() => ({
        all: products.length,
        available: products.filter((p) => p.status === "available").length,
        coming_soon: products.filter((p) => p.status === "coming_soon").length,
        sold_out: products.filter((p) => p.status === "sold_out").length,
        custom: products.filter((p) => p.is_custom).length,
    }), [products]);

    const visible = useMemo(() => {
        if (filter === "all") return products;
        if (filter === "custom") return products.filter((p) => p.is_custom);
        return products.filter((p) => p.status === filter);
    }, [products, filter]);

    const handleEdit = (p) => {
        setEditingProduct(p);
        setIsNew(false);
        setModalOpen(true);
    };
    const handleCreate = () => {
        setEditingProduct(null);
        setIsNew(true);
        setModalOpen(true);
    };
    const handleReset = async (p) => {
        if (!window.confirm(`Discard all edits to "${p.name}" and restore original values?`)) return;
        try {
            await axios.post(`${API}/admin/products/${p.id}/reset`, null, {
                headers: { "X-Admin-Password": password },
            });
            toast.success("Restored to original");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        }
    };
    const handleDelete = async (p) => {
        if (!window.confirm(`Permanently delete custom product "${p.name}"?`)) return;
        try {
            await axios.delete(`${API}/admin/products/${p.id}`, {
                headers: { "X-Admin-Password": password },
            });
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div data-testid="products-tab">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-2">
                    {[
                        { k: "all", label: "All" },
                        { k: "available", label: "Live" },
                        { k: "coming_soon", label: "Coming Soon" },
                        { k: "sold_out", label: "Sold Out" },
                        { k: "custom", label: "Custom" },
                    ].map((f) => {
                        const active = filter === f.k;
                        return (
                            <button
                                key={f.k}
                                data-testid={`products-filter-${f.k}`}
                                onClick={() => setFilter(f.k)}
                                className={`text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border transition-colors ${
                                    active
                                        ? "bg-[#E63946] border-[#E63946] text-white"
                                        : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                                }`}
                            >
                                {f.label} <span className="opacity-70">({counts[f.k] ?? 0})</span>
                            </button>
                        );
                    })}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="products-export-seed"
                        onClick={async () => {
                            if (!window.confirm(
                                "Snapshot the current catalog as the bundled seed?\n\n" +
                                "This rewrites /app/backend/seed_products.py so that on next REDEPLOY, " +
                                "production (fgmapparel.com) ships with this exact catalog. The current " +
                                "seed file is automatically backed up to .py.bak in case you need to revert."
                            )) return;
                            try {
                                const r = await axios.post(`${API}/admin/catalog/export-seed`, null, {
                                    headers: { "X-Admin-Password": password },
                                });
                                toast.success(`Exported ${r.data.products} products to seed · Redeploy to push live`);
                            } catch (err) {
                                toast.error(err?.response?.data?.detail || "Export failed");
                            }
                        }}
                        title="Snapshot the current catalog as the bundled seed (then redeploy)"
                        className="text-[10px] font-display tracking-[0.25em] uppercase px-4 py-2 bg-fgm-dark border border-[#E63946]/60 text-[#E63946] hover:bg-[#E63946] hover:text-white transition-colors"
                    >
                        ↗ Export as Seed
                    </button>
                    <button
                        data-testid="products-new"
                        onClick={handleCreate}
                        className="text-[10px] font-display tracking-[0.25em] uppercase px-4 py-2 bg-[#E63946] text-white hover:bg-white hover:text-[#E63946] transition-colors"
                    >
                        + New Product
                    </button>
                </div>
            </div>

            {loading && <div className="text-zinc-500 text-sm py-12 text-center">Loading…</div>}

            {!loading && visible.length === 0 && (
                <div className="bg-fgm-surface border border-white/10 p-12 text-center text-zinc-500 font-display tracking-widest uppercase">
                    No products match this filter
                </div>
            )}

            {!loading && visible.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {visible.map((p) => (
                        <ProductCard
                            key={p.id}
                            p={p}
                            onEdit={handleEdit}
                            onReset={handleReset}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            <ProductModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                product={editingProduct}
                isNew={isNew}
                password={password}
                onSaved={load}
            />
        </div>
    );
}
